<script setup lang="ts">
import {
    Button,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    TagsInput,
    TagsInputItem,
    TagsInputItemDelete,
    TagsInputItemText,
    ZhEmptyState,
    ZhPageTitle,
    ZhPanel,
    ZhSectionHeading,
    ZhSystemLabel,
} from '@zerohuman/ui'
import { computed, ref, watch } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { api, type Member, type RoleCatalogStanding, type RoleDetail, type RoleShareReview, type TaskRow } from '@/api'
import HolderToolGaps from '@/components/HolderToolGaps.vue'
import RoleKpis from '@/components/RoleKpis.vue'
import RoleShareDialog from '@/components/RoleShareDialog.vue'
import TaskGraph from '@/components/TaskGraph.vue'
import { useRouteIdGuard } from '@/composables/useRouteIdGuard'
import { useVisiblePolling } from '@/composables/useVisiblePolling'
import type { HolderToolGapsEntry } from '@/lib/holderToolGaps'
import { memberLabel } from '@/lib/memberLabel'

/*
 * Every control, action and surface here is the kit's (#1225): both pickers are the kit `Select`, the team link a
 * kit `Button` in link form, the graph frame a kit canvas panel and its empty state the kit's. The screen states no
 * colour, radius, border or fill of its own. TaskGraph and RoleKpis are portal-wide and are migrated under their
 * own items; here they only sit on the surfaces.
 */
const route = useRoute()
const router = useRouter()
const detail = ref<RoleDetail | null>(null)
const members = ref<Member[]>([])
const allTasks = ref<TaskRow[]>([])
const error = ref<string | null>(null)
const busy = ref(false)
const managingTasks = ref(false)
/**
 * The pickers hold no member or task: picking one adds it at once, so each always reads its placeholder.
 * Held as refs so the kit Selects are bound (controlled) and go back to empty after every pick.
 */
const pickMember = ref('')
const pickTask = ref('')
const workflowTasks = computed(() => (detail.value?.tasks ?? []).filter(task => task.builtInRole !== 'analysis'))
const roleTasks = computed(() =>
    (detail.value?.tasks ?? []).filter(
        task => task.assigneeRole === detail.value?.slug && task.builtInRole !== 'analysis',
    ),
)
const availableMembers = computed(() => {
    const held = new Set(detail.value?.members.map(member => member.id) ?? [])
    return members.value.filter(member => !held.has(member.id))
})
const availableTasks = computed(() => {
    const onRole = new Set(roleTasks.value.map(task => task.id))
    return allTasks.value.filter(task => !onRole.has(task.id) && task.builtInRole !== 'analysis')
})
const isCurrentRoute = useRouteIdGuard()
let inFlight = false
/**
 * #637: for each holder, the tools their roles' tasks (this role's among them) need that no binding of theirs reaches,
 * read from the API when the page opens and again for a member once their add is saved, so a role whose holder still
 * misses a tool says so on every visit, not only straight after the add. While a read is out the list says it is
 * checking; a failed read says so with Try again, and never undoes the add.
 */
const holderGaps = ref<HolderToolGapsEntry[]>([])

watch(
    () => String(route.params.id),
    async (roleId, _previous, onCleanup) => {
        let current = true
        onCleanup(() => {
            current = false
        })
        detail.value = null
        error.value = null
        holderGaps.value = []
        try {
            const [loaded, people, tasksPage] = await Promise.all([
                api.role(roleId),
                api.members(),
                api.tasks({ limit: 200 }),
            ])
            if (current) {
                detail.value = loaded
                members.value = people
                allTasks.value = tasksPage.data
                void Promise.all(loaded.members.map(member => showToolGaps(member.id)))
            }
        } catch {
            if (current) error.value = 'Role not found.'
        }
    },
    { immediate: true },
)

/**
 * A silent refresh never blanks what is already on screen: it reassigns `detail` so the graph's
 * nodes re-render in place (no Vue Flow teardown, no pan/zoom loss), and swallows a failed
 * background fetch instead of replacing a working graph with an error state.
 */
async function refreshSilently(): Promise<void> {
    if (inFlight) {
        return
    }
    const roleId = String(route.params.id)
    inFlight = true
    try {
        const loaded = await api.role(roleId)
        // Ignore a response for a role the page has already navigated away from.
        if (isCurrentRoute(roleId)) {
            detail.value = loaded
            error.value = null
        }
    } catch {
        // A background failure leaves the graph that is already loaded exactly as it is.
    } finally {
        inFlight = false
    }
}

useVisiblePolling(() => void refreshSilently())

function openRole(roleId: string) {
    void router.push(`/roles/${roleId}`)
}

function openTask(taskId: string) {
    void router.push(`/tasks/${taskId}`)
}

async function apply(change: () => Promise<RoleDetail>): Promise<void> {
    busy.value = true
    error.value = null
    try {
        detail.value = await change()
    } catch (caught) {
        error.value = caught instanceof Error ? caught.message : 'Could not update role.'
    } finally {
        busy.value = false
    }
}

/** A kit Select's choice, said as the id it carries; an empty or cleared choice is no choice. */
function chosenId(choice: unknown): string {
    return choice === null || choice === undefined ? '' : String(choice)
}

/** The choice arrives from the kit Select's model update; the picker is never left holding it. */
async function addMember(choice: unknown): Promise<void> {
    const memberId = chosenId(choice)
    const roleId = detail.value?.id
    pickMember.value = ''
    if (!memberId || !roleId) {
        return
    }
    await apply(() => api.addRoleMember(roleId, memberId))
    if (!detail.value?.members.some(member => member.id === memberId)) {
        return
    }
    await showToolGaps(memberId)
}

function holderLabel(memberId: string): string {
    const known = members.value.find(member => member.id === memberId)
    return known ? memberLabel(known) : memberName(memberId)
}

/** Puts one holder's list in place, keeping where it sits; a holder new to the list goes last. */
function putHolderGaps(entry: HolderToolGapsEntry): void {
    const at = holderGaps.value.findIndex(row => row.memberId === entry.memberId)
    holderGaps.value =
        at === -1 ? [...holderGaps.value, entry] : holderGaps.value.map((row, index) => (index === at ? entry : row))
}

async function showToolGaps(memberId: string): Promise<void> {
    const roleId = detail.value?.id
    putHolderGaps({ memberId, memberName: holderLabel(memberId), gaps: [], state: 'checking' })
    let entry: HolderToolGapsEntry
    try {
        entry = { memberId, memberName: holderLabel(memberId), gaps: await api.memberToolGaps(memberId) }
    } catch {
        entry = { memberId, memberName: holderLabel(memberId), gaps: [], state: 'failed' }
    }
    // A read that lands after the page moved to another role is dropped; so is one for a holder since taken off.
    if (detail.value?.id !== roleId) {
        return
    }
    if (!detail.value?.members.some(member => member.id === memberId)) {
        holderGaps.value = holderGaps.value.filter(row => row.memberId !== memberId)
        return
    }
    putHolderGaps(entry)
}

function taskName(taskId: unknown): string {
    return (
        roleTasks.value.find(task => task.id === taskId)?.name ||
        roleTasks.value.find(task => task.id === taskId)?.slug ||
        String(taskId)
    )
}

function memberName(memberId: unknown): string {
    return detail.value?.members.find(member => member.id === memberId)?.name ?? String(memberId)
}

async function removeMember(memberId: unknown): Promise<void> {
    const roleId = detail.value?.id
    if (!roleId) {
        return
    }
    await apply(() => api.removeRoleMember(roleId, String(memberId)))
    holderGaps.value = holderGaps.value.filter(row => detail.value?.members.some(member => member.id === row.memberId))
}

/** The choice arrives from the kit Select's model update; the picker is never left holding it. */
async function addTask(choice: unknown): Promise<void> {
    const taskId = chosenId(choice)
    const roleId = detail.value?.id
    pickTask.value = ''
    if (!taskId || !roleId) {
        return
    }
    await apply(() => api.addRoleTask(roleId, taskId))
}

async function removeTask(taskId: unknown): Promise<void> {
    const roleId = detail.value?.id
    if (!roleId) {
        return
    }
    await apply(() => api.removeRoleTask(roleId, String(taskId)))
}

/*
 * Publishing the role to the catalog, and keeping what it lists up to date (#1844). The page reads the role's standing
 * from the role it loads, by the rule the team share review reads: a role the catalog holds is said to be there, one
 * whose public name another publisher holds says so and cannot be published, and any other is offered "Publish to
 * catalog". A role this company has in the catalog offers "Update catalog" instead, and says how many changes its
 * listing is behind on (`catalog.changes`): tasks with a newer version, tasks not decided on yet, and tasks that no
 * longer belong on it. Both open `RoleShareDialog`, where each task is ticked in or out. A name held elsewhere says
 * why the action is disabled and where a role is renamed (Chat).
 */
const NOT_IN_CATALOG: RoleCatalogStanding = { public: false, heldElsewhere: false }
const catalogStanding = computed<RoleCatalogStanding>(() => detail.value?.catalog ?? NOT_IN_CATALOG)
const catalogChanges = computed(() => catalogStanding.value.changes ?? 0)
const publishOpen = ref(false)
/** The role this page has just published, said once on the page after the dialog closes. */
const publishedRoleId = ref<string | null>(null)
const justPublished = computed(() => publishedRoleId.value !== null && publishedRoleId.value === detail.value?.id)
watch(
    () => String(route.params.id),
    () => {
        publishedRoleId.value = null
        publishOpen.value = false
    },
)

function openPublish(): void {
    publishOpen.value = true
}

/** The dialog saved the listing: the page takes its standing from the review, then reloads the role quietly. */
function onListingSaved(review: RoleShareReview): void {
    const role = detail.value
    if (!role || role.id !== review.role.id) return
    if (!catalogStanding.value.public && review.catalog.public) publishedRoleId.value = role.id
    detail.value = { ...role, catalog: { ...review.catalog, changes: review.changes } }
    void refreshSilently()
}
</script>

<template>
	<section class="w-full space-y-6 p-4 md:p-8">
		<!--
			A failed load leaves no role to show, so its error stands alone; a rejected change is said above the role
			it failed on, which stays on screen and usable.
		-->
		<p v-if="error" class="text-destructive text-sm">{{ error }}</p>
		<template v-if="detail">
			<p class="text-muted-foreground text-sm">
				<RouterLink class="underline" to="/roles">Roles</RouterLink>
			</p>
			<div class="flex flex-wrap items-center justify-between gap-3">
				<div>
					<ZhPageTitle kicker="Role">{{ detail.name }}</ZhPageTitle>
					<p class="mt-1 text-muted-foreground text-sm">{{ detail.slug }} · Role</p>
				</div>
				<div class="flex flex-wrap gap-2">
					<Button type="button" size="sm" variant="outline" data-testid="manage-tasks" @click="managingTasks = !managingTasks">
						{{ managingTasks ? 'Done' : 'Manage tasks' }}
					</Button>
					<!-- The role's standing with the catalog (#1844): there already, or its name held by another publisher. -->
					<ZhSystemLabel
						v-if="catalogStanding.heldElsewhere"
						as="span"
						tone="system"
						class="self-center"
						data-testid="role-publish-state"
					>
						Held by another publisher
					</ZhSystemLabel>
					<ZhSystemLabel
						v-else-if="catalogStanding.public"
						as="span"
						tone="muted"
						class="self-center"
						data-testid="role-publish-state"
					>
						In the catalog
					</ZhSystemLabel>
					<ZhSystemLabel
						v-if="catalogStanding.public && !catalogStanding.heldElsewhere && catalogChanges > 0"
						as="span"
						tone="system"
						class="self-center"
						data-testid="role-catalog-changes"
					>
						{{ catalogChanges }} change{{ catalogChanges === 1 ? '' : 's' }} not in the catalog
					</ZhSystemLabel>
					<Button
						v-if="!catalogStanding.public || catalogStanding.heldElsewhere"
						type="button"
						size="sm"
						variant="outline"
						data-testid="role-publish"
						:disabled="catalogStanding.heldElsewhere"
						@click="openPublish"
					>Publish to catalog</Button>
					<Button
						v-else
						type="button"
						size="sm"
						variant="outline"
						data-testid="role-catalog-update"
						@click="openPublish"
					>Update catalog</Button>
				</div>
			</div>
			<!--
				Why the action is disabled, and what to do instead: public role names are one per catalog, and a role is
				renamed through Chat (`os.update_role`), since this page has no rename of its own.
			-->
			<p v-if="catalogStanding.heldElsewhere" class="text-muted-foreground text-sm" data-testid="role-publish-held">
				Another publisher already has a public role called “{{ detail.slug }}” in the catalog, so this role can’t be
				published under that name.
				<RouterLink class="underline" to="/chat">Ask in Chat to rename it</RouterLink>, then publish it here.
			</p>
			<p v-else-if="justPublished && catalogStanding.public" class="text-sm" role="status" data-testid="role-publish-done">
				Published. Any company can now find {{ detail.name }} and the tasks you ticked in the catalog.
			</p>
			<RoleShareDialog v-model:open="publishOpen" :role-id="detail.id" @saved="onListingSaved" />
			<ZhPanel
				v-if="managingTasks"
				class="space-y-2"
				data-testid="manage-tasks-panel"
				aria-labelledby="role-manage-tasks-heading"
			>
				<ZhSectionHeading id="role-manage-tasks-heading">Tasks on this role</ZhSectionHeading>
				<p class="text-muted-foreground text-sm">Tasks with this role run for members who hold it.</p>
				<div class="flex flex-wrap items-center gap-2">
					<TagsInput
						:model-value="roleTasks.map(task => task.id)"
						:display-value="taskName"
						:disabled="busy"
						class="min-w-48 flex-1"
						@remove-tag="removeTask"
					>
						<TagsInputItem v-for="task in roleTasks" :key="task.id" :value="task.id">
							<TagsInputItemText />
							<TagsInputItemDelete tabindex="0" :aria-label="`Remove ${task.name || task.slug}`" />
						</TagsInputItem>
						<span v-if="!roleTasks.length" class="text-muted-foreground px-1">No tasks on this role.</span>
					</TagsInput>
					<!-- Picking a task adds it at once, so the picker never holds one: it always reads "Add task…". -->
					<template v-if="availableTasks.length">
						<span id="role-add-task-label" class="sr-only">Add task</span>
						<!-- `aria-label` and the test id land on the field the kit Select mirrors its choice on. -->
						<Select
							:model-value="pickTask"
							:disabled="busy"
							aria-label="Add task"
							data-testid="add-role-task"
							@update:model-value="addTask"
						>
							<SelectTrigger class="min-w-40" aria-labelledby="role-add-task-label">
								<SelectValue placeholder="Add task…" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem v-for="task in availableTasks" :key="task.id" :value="task.id">
									{{ task.name || task.slug }}
								</SelectItem>
							</SelectContent>
						</Select>
					</template>
				</div>
			</ZhPanel>
			<ZhPanel class="space-y-3" aria-labelledby="role-workflow-heading">
				<div>
					<ZhSectionHeading id="role-workflow-heading">Task workflow</ZhSectionHeading>
					<p class="text-muted-foreground text-sm">Follow this role's tasks and their immediate incoming and outgoing handoffs.</p>
				</div>
				<!-- The graph's frame is a surface of its own; the kit gives a canvas its height. -->
				<ZhPanel v-if="workflowTasks.length" as="div" flush canvas data-testid="role-task-graph">
					<TaskGraph :tasks="workflowTasks" :related-tasks="detail.relatedTasks" scope="role" @select="openTask" @select-role="openRole" />
				</ZhPanel>
				<ZhEmptyState v-else label="No tasks">No tasks run as this role.</ZhEmptyState>
			</ZhPanel>
			<ZhPanel v-if="detail.teams?.length" class="space-y-2" aria-labelledby="role-teams-heading" data-testid="role-teams">
				<ZhSectionHeading id="role-teams-heading">Held on</ZhSectionHeading>
				<p class="text-muted-foreground text-sm">A role belongs to no team: its holders run its tasks on whatever team they sit, and each team they sit on runs those tasks.</p>
				<ul class="space-y-1 text-sm">
					<li v-for="team in detail.teams" :key="team.id">
						<RouterLink :to="`/teams/${team.id}`" class="underline-offset-2 hover:underline">{{ team.name }}</RouterLink>
						<span class="text-muted-foreground"> · {{ team.holders.map(holder => holder.name).join(', ') }}</span>
					</li>
				</ul>
			</ZhPanel>
			<RoleKpis :role-id="detail.id" />
			<ZhPanel class="space-y-2" aria-labelledby="role-members-heading">
				<ZhSectionHeading id="role-members-heading">Members</ZhSectionHeading>
				<TagsInput
					:model-value="detail.members.map(member => member.id)"
					:display-value="memberName"
					:disabled="busy"
					@remove-tag="removeMember"
				>
					<TagsInputItem v-for="member in detail.members" :key="member.id" :value="member.id">
						<TagsInputItemText />
						<TagsInputItemDelete tabindex="0" :aria-label="`Remove ${member.name}`" />
					</TagsInputItem>
					<!-- The picker sits in the tag list, in the kit's dense size; picking a member adds them at once. -->
					<template v-if="availableMembers.length">
						<span id="role-add-member-label" class="sr-only">Add member</span>
						<Select
							:model-value="pickMember"
							:disabled="busy"
							aria-label="Add member"
							data-testid="add-role-member"
							@update:model-value="addMember"
						>
							<SelectTrigger size="sm" class="min-w-32" aria-labelledby="role-add-member-label">
								<SelectValue placeholder="Add member…" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem v-for="member in availableMembers" :key="member.id" :value="member.id">
									{{ memberLabel(member) }}
								</SelectItem>
							</SelectContent>
						</Select>
					</template>
					<span v-else-if="!detail.members.length" class="text-muted-foreground px-1">No member holds this role.</span>
				</TagsInput>
				<HolderToolGaps
					:entries="holderGaps"
					section-testid="holder-tool-gaps"
					row-testid="holder-tool-gap"
					banner
					@retry="showToolGaps"
				/>
			</ZhPanel>
		</template>
	</section>
</template>
